import api from './api';
import { unwrapApiResponse } from '@/utils/api-response.util';
import { SelfActivityReport, ReportOptions, AiReportListItem, AiReportDetail } from '@/types/client-reports';

export type ReportSection = 'profile' | 'measurements' | 'metrics' | 'goals' | 'events' | 'files' | 'appointments' | 'questionnaires';

export const getReportOptions = async (): Promise<ReportOptions> => {
  const response = await api.get('/reports/activity/options');
  return unwrapApiResponse<ReportOptions>(response.data);
};

export const getSelfActivityReport = async (
  from: string,
  to: string,
  sections?: ReportSection[],
  eventTypeIds?: number[],
): Promise<SelfActivityReport> => {
  const response = await api.post('/reports/activity', {
    from,
    to,
    ...(sections && sections.length > 0 ? { sections } : {}),
    ...(eventTypeIds && eventTypeIds.length > 0 ? { eventTypeIds: eventTypeIds.join(',') } : {}),
  });
  // Відповідь: { success, meta: { clientId, summary, period, ... }, data: { profile, events, ... } }
  const { meta, data } = response.data;
  return { ...meta, ...data } as SelfActivityReport;
};

export const getSelfAiContext = async (
  from: string,
  to: string,
  sections?: ReportSection[],
): Promise<string> => {
  const response = await api.post('/reports/activity/ai-context', {
    from,
    to,
    ...(sections && sections.length > 0 ? { sections } : {}),
  });
  return unwrapApiResponse<{ markdown: string }>(response.data).markdown;
};

export interface AiAnalysisResult {
  html: string;
  fromCache: boolean;
}

export const getSelfAiAnalysis = async (
  from: string,
  to: string,
  sections?: ReportSection[],
  prompt?: string,
): Promise<AiAnalysisResult> => {
  try {
    const response = await api.post('/reports/activity/ai-analysis', {
      from,
      to,
      ...(sections && sections.length > 0 ? { sections } : {}),
      ...(prompt ? { prompt } : {}),
    });
    const data = unwrapApiResponse<{ html: string; fromCache?: boolean }>(response.data);
    return { html: data.html, fromCache: data.fromCache ?? false };
  } catch (e: any) {
    const msg = e?.response?.data?.message;
    throw new Error(msg ?? e?.message ?? 'Не вдалося отримати AI-аналіз');
  }
};

export interface AiReportsPage {
  data: AiReportListItem[];
  meta: { total: number; limit: number; offset: number };
}

export const getMyAiReports = async (limit: number, offset: number): Promise<AiReportsPage> => {
  const response = await api.get('/reports/ai', { params: { limit, offset } });
  const { data, meta } = response.data;
  return { data, meta };
};

export const getMyAiReport = async (id: number): Promise<AiReportDetail> => {
  const response = await api.get(`/reports/ai/${id}`);
  return unwrapApiResponse<AiReportDetail>(response.data);
};

export const downloadSelfActivityReportPdf = async (
  from: string,
  to: string,
  sections?: ReportSection[],
  eventTypeIds?: number[],
): Promise<void> => {
  const response = await api.post(
    '/reports/activity/pdf',
    {
      from,
      to,
      ...(sections && sections.length > 0 ? { sections } : {}),
      ...(eventTypeIds && eventTypeIds.length > 0 ? { eventTypeIds: eventTypeIds.join(',') } : {}),
    },
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `my-report_${from}_${to}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};
